import {
  KeyboardEvent,
  MouseEvent,
  cloneElement,
  forwardRef,
  useEffect,
  useState
} from 'react';
import { checkInputMask, getTextMask } from './mask';

import { Icon } from '../jdsicon/generated';
import { JDSInputFieldProps } from './generated';
import { useRootRef } from '../../hooks/useRootRef';

export interface InputFieldProps
  extends Omit<
    JDSInputFieldProps,
    | 'value'
    | 'onFocus'
    | 'onBlur'
    | 'onChange'
    | 'onKeyDown'
    | 'onInvalid'
    | 'onPrefixClick'
    | 'onSuffixClick'
    | 'prefix'
    | 'suffix'
    | 'maxLength'
  > {
  disabled?: boolean;
  state?: 'none' | 'success' | 'error' | 'warning';
  label?: string;
  id?: string;
  value?: string | number | boolean;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  pattern?: string;
  /**
   * @deprecated use `stateText`
   */
  stateConfig?: {
    errorText?: string;
    successText?: string;
    warningText?: string;
    requiredText?: string;
  };
  prefix?: string | JSX.Element;
  suffix?: string | JSX.Element;
  onFocus?: React.HTMLAttributes<HTMLInputElement>['onFocus'];
  onBlur?: React.HTMLAttributes<HTMLInputElement>['onBlur'];
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  onPrefixClick?: (ev: MouseEvent<HTMLDivElement>) => void;
  onSuffixClick?: (value: MouseEvent<HTMLDivElement>) => void;
  onInvalid?: (ev: React.InvalidEvent<HTMLInputElement>) => void;
  readonly?: boolean;
  /**
   * When used with text based input, determines the input format.
   * e.g. XY## #### to allow `XY12 345` values.
   * <br/>
   * `#`: `0-9`
   * <br/>
   * `&`: `A-Z, a-z`
   * <br/>
   * `*`: `A-Z, a-z, 0-9`
   * <br/>
   * `pattern` prop is also set internally to assist with invalid error states. You can overwrite this by passing explicit `pattern' prop.
   * This will not work on `checkbox`, `radio`, `toggle`, `number`, `code` and `textarea`.
   */
  textMask?: string | Array<string | RegExp>;
}

type StateType = InputFieldProps['state'] | 'required' | 'none';

const stateValues = {
  error: { state: 'error', stateText: 'errorText' },
  success: { state: 'success', stateText: 'successText' },
  warning: { state: 'warning', stateText: 'warningText' },
  required: { state: 'error', stateText: 'requiredText' }
} as Record<
  NonNullable<StateType>,
  {
    state: NonNullable<StateType>;
    stateText: keyof NonNullable<InputFieldProps['stateConfig']>;
  }
>;

/**
 * Manages the focus state of the Input
 * @returns
 * focused: The input state for the label. This considers the placeholder and the input's current value
 * _inputFocused: the actual focus state of the input
 */
export const useFocusedLabel = (
  props: InputFieldProps,
  ref?: HTMLDivElement | null
) => {
  const [focused, setFocused] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const labelRef =
    ref?.querySelector('.j-JDSInputField-input_label-jds-text') ||
    ref?.querySelector('.j-JDSTextarea-input_label-jds-text');

  useEffect(() => {
    if (props.placeholder || props.value) {
      labelRef?.classList?.add('position-top');
    }
  }, [props.placeholder, labelRef, props.value]);

  return {
    onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
      setFocused(true);
      labelRef?.classList.add('position-top');
      props.onChange?.(ev);
    },
    onBlur: (ev: React.FocusEvent<HTMLInputElement>) => {
      if (!(ev.target.value || props.placeholder)) {
        setFocused(false);
        labelRef?.classList.remove('position-top');
      }
      props.onBlur?.(ev);
      setInputFocused(false);
    },
    onFocus: (ev: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      setInputFocused(true);
      props.onFocus?.(ev);
      labelRef?.classList.add('position-top');
    },
    focused: Boolean(props.value) || Boolean(props.placeholder) || focused,
    _inputFocused: inputFocused
  };
};

export const useInputState = (
  state: InputFieldProps['state'],
  required?: boolean,
  onInvalid?: (ev: React.InvalidEvent<HTMLInputElement>) => void,
  stateConfig: InputFieldProps['stateConfig'] = {},
  stateText?: string
) => {
  const [inputState, setInputState] = useState<NonNullable<StateType>>('none');
  useEffect(() => {
    if (!required) {
      setInputState(state || 'none');
    }
  }, [state, required]);
  const stateKey = stateValues?.[inputState || 'none']?.state;
  const stateTextKey = stateValues[inputState]?.stateText;

  return {
    onInvalid: (ev: React.InvalidEvent<HTMLInputElement>) => {
      onInvalid?.(ev);
      if (required) {
        setInputState('required');
      } else {
        setInputState('error');
      }
    },
    inputState: state || stateKey,
    stateText: stateText || stateConfig?.[stateTextKey]
  };
};

export const getRequiredLabel = (label = '', required?: boolean) => {
  return label + (required ? ' *' : '');
};

export const getIcon = (
  icon?: string | JSX.Element,
  onClick?: (ev: React.MouseEvent<HTMLDivElement>) => void
): JSX.Element | undefined => {
  if (!icon) return undefined;
  if (typeof icon === 'string')
    return <Icon onClick={onClick} ic={icon} color='grey-80' />;
  if (icon.type.displayName) return cloneElement(icon, { color: 'grey-80' });
  if (icon.props?.ic)
    return (
      <Icon
        onClick={onClick}
        ic={icon.props.ic}
        color='grey-80'
        {...icon.props}
      />
    );
  return <Icon onClick={onClick} ic={icon} color='grey-80' />;
};

const getPrefixWidth = (element: HTMLElement | null) => {
  const prefixContainer = element?.querySelector('div#prefix-container');
  const { width } = prefixContainer?.getBoundingClientRect() || {};
  return width || 0;
};

export const withLogic = (
  Component: React.ComponentType<InputFieldProps>
): React.FC<InputFieldProps> =>
  forwardRef<HTMLInputElement, InputFieldProps>(
    (props: InputFieldProps, ref) => {
      const { rootRef, element } = useRootRef();
      const { focused, _inputFocused, ...rest } = useFocusedLabel(
        props,
        element
      );
      const { inputState, onInvalid, stateText } = useInputState(
        props.state,
        props.required,
        props.onInvalid,
        props.stateConfig
      );

      const handlePrefixClick = (event: React.MouseEvent<HTMLDivElement>) => {
        props.onPrefixClick?.(event);
      };
      const handleSuffixClick = (event: React.MouseEvent<HTMLDivElement>) => {
        props.onSuffixClick?.(event);
      };

      return (
        <Component
          {...{
            ...props,
            ...rest,
            rootRef,
            prefix: getIcon(props.prefix, handlePrefixClick),
            suffix: getIcon(props.suffix, handleSuffixClick),
            onInvalid,
            _labelOffset: getPrefixWidth(element) + 8,
            onKeydown: (ev: KeyboardEvent<HTMLInputElement>) => {
              const allowedCharsForNumber = [
                '1',
                '2',
                '3',
                '4',
                '5',
                '6',
                '7',
                '8',
                '9',
                '0',
                'e',
                '-',
                '+',
                '.'
              ];
              const allowedCharsForPhone = [
                '1',
                '2',
                '3',
                '4',
                '5',
                '6',
                '7',
                '8',
                '9',
                '0',
                '(',
                ')',
                '-',
                ' ',
                '+'
              ];

              if (['number', 'phone'].includes(props.type)) {
                if (
                  ['Backspace', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(
                    ev.key
                  )
                ) {
                  return;
                }

                const allowedChars =
                  props.type === 'number'
                    ? allowedCharsForNumber
                    : allowedCharsForPhone;
                !allowedChars.includes(ev.key) && ev.preventDefault();
              }

              if (
                (props.pattern && typeof props.value === 'string') ||
                props.textMask
              ) {
                const action = checkInputMask(
                  ev,
                  props.value,
                  getTextMask(props.textMask as string),
                  props.readonly,
                  props.pattern
                );

                if (action === 'prevent' || action === 'back') {
                  ev.preventDefault();
                  if (action === 'back') {
                    const currentValue = (ev.currentTarget.value || '')?.slice(
                      0,
                      -1
                    );
                    const tracker = Object.getOwnPropertyDescriptor(
                      window.HTMLInputElement.prototype,
                      'value'
                    )?.set;
                    tracker?.call(ev.target, currentValue);

                    const event = {
                      ...ev,
                      target: {
                        ...ev.target,
                        value: currentValue
                      }
                    };
                    props.onChange?.(event as any);
                  }
                  return;
                }
              }
              props.onKeyDown?.(ev);
            },
            label: getRequiredLabel(props.label, props.required),
            _inputFocus: _inputFocused,
            state: inputState,
            stateText: props.stateText || stateText,
            _hasPrefix: Boolean(props.prefix),
            _hasSuffix: Boolean(props.suffix),
            _labelFocus: focused,
            layerRefs: { input: ref }
          }}
        />
      );
    }
  );
